import React, {useCallback, useEffect, useState} from 'react'
import {useFormikContext} from 'formik'
import {useTranslation} from 'react-i18next'
import {MailField, AutosuggestionMailInput} from '../MailField'
import {FieldComponent} from 'components/Shared/FieldComponent'
import {momentToISOLocalDate} from 'utils/date'
import moment from 'moment'
import {CreateContractorDialog} from 'components/Contractors'
import ContractorRole from 'constants/ContractorRole'
import Sections from 'components/Order/sections'
import {FormColumn} from 'components/Shared/CrudComponents'
import {
    ORDER_CONTRACTOR_READ,
    ORDER_CONTRACTOR_WRITE,
    ORDER_DATA_READ,
    ORDER_DATA_WRITE,
    useAuth
} from 'context/auth'
import {fetchContractor, fetchContractorBalance, fetchContractorEmails} from 'services/ContractorService'
import {useErrorHandling} from 'context/errorHandling'
import {formatMerchantLimitMessage} from 'components/Order/OrderDetails/Utils'
import {ContractorSelectField} from '../../../../Carrier/CarrierSelectField'
import {ReadOnlyCheckbox} from '../../../../Shared/ReadOnlyCheckbox'

export function ClientDataForm({onContractorCreated, onPayerSelect, onFullClientData}) {
    const {values, touched, setFieldValue, setFieldTouched} = useFormikContext()
    const {hasAnyPrivileges} = useAuth()

    const [createClientWithName, setCreateClientWithName] = useState()
    const [createPayerWithName, setCreatePayerWithName] = useState()
    const [clientBalance, setClientBalance] = useState(null)
    const [clientCopy, setClientCopy] = useState(null)
    const [clientMailSuggestions, setClientMailSuggestions] = useState([])
    const [payer, setPayer] = useState(null)

    const {t} = useTranslation()
    const {handleError} = useErrorHandling()

    useEffect(() => {
        if (payer && touched.contractorInfo?.payerId && !touched.paymentDate) {
            const payerInvoiceDueDays = payer.invoiceDueDays
            setFieldValue('paymentDate', momentToISOLocalDate(moment().add(payerInvoiceDueDays, 'd')))
        }
    }, [payer, setFieldValue, touched])

    useEffect(() => {
        if (clientCopy) {
            onFullClientData(clientCopy)
        }
    }, [clientCopy,onFullClientData])

    const formatPayerLimitMessage = useCallback(() => {
        if (payer) {
            return formatMerchantLimitMessage(payer, clientBalance, t)
        }
    }, [payer, clientBalance, t])

    useEffect(() => {
        if (payer) {
            fetchContractorBalance(payer.id)
                .then(setClientBalance)
                .catch(handleError)
        }
    }, [payer, handleError])



    useEffect(() => {
        const clientId = values.contractorInfo.clientId
        if (clientId) {
            fetchContractor(clientId)
                .then(setClientCopy)
                .catch(handleError)
            fetchContractorEmails(clientId)
                .then(result => setClientMailSuggestions(result.map(it => it.email)))
                .catch(handleError)
        }
    }, [values.contractorInfo.clientId, handleError])

    const onClientChange = (autocompleteFieldName) => (selectedClientId, option) => {
        if (!!selectedClientId) {
            setFieldValue(autocompleteFieldName, selectedClientId)
            setFieldTouched(autocompleteFieldName, true, false)
            if (autocompleteFieldName === 'contractorInfo.payerId') {
                onPayerSelect(selectedClientId, option)
            }
        }
    }


    function contractorCreated(contractor) {
        if (contractor) {
            onContractorCreated()
            if (createClientWithName) {
                setFieldValue('contractorInfo.clientId', contractor.id)
                onClientChange('contractorInfo.payerId')(contractor.id)

            } else {
                setFieldValue('contractorInfo.payerId', contractor.id)
                onClientChange('contractorInfo.clientId')(contractor.id)
            }
        }
        setCreatePayerWithName(null)
        setCreateClientWithName(null)
    }

    const hasOrderDataReadPrivilege = hasAnyPrivileges([ORDER_DATA_READ])
    const hasContractorReadPrivilege = hasAnyPrivileges([ORDER_CONTRACTOR_READ])

    return <FormColumn section={Sections.ClientData} disabled={!hasAnyPrivileges([ORDER_CONTRACTOR_WRITE])}>
        {hasContractorReadPrivilege && <CreateContractorDialog
            requiredRoles={[ContractorRole.CLIENT]}
            open={!!(createClientWithName || createPayerWithName)}
            newContractorName={createClientWithName || createPayerWithName}
            onClose={contractorCreated}/>
        }
        {values.readableId && hasOrderDataReadPrivilege &&
            <FieldComponent disabled fieldName={'readableId'} label={'form.order.clientData.orderNumber'}/>
        }
        {hasOrderDataReadPrivilege &&
            <div>
                <FieldComponent disabled={!hasAnyPrivileges([ORDER_DATA_WRITE])} fieldName={'referenceId'}
                                label={'form.order.clientData.referenceId'}/>
            </div>
        }
        {hasContractorReadPrivilege &&
            [
                <ContractorSelectField
                    fieldName="contractorInfo.clientId"
                    label={t('form.order.clientData.client')}
                    onChange={(newValue, newSelectedOption) => {
                        onClientChange('contractorInfo.clientId')(newValue, newSelectedOption)
                        onClientChange('contractorInfo.payerId')(newValue, newSelectedOption)
                    }}
                    onContractorCreated={onContractorCreated}
                    role={"CLIENT"}
                />,
                <ContractorSelectField
                    fieldName="contractorInfo.payerId"
                    label={t('form.order.clientData.payer')}
                    onChange={(newValue, newSelectedOption) => {
                        onClientChange('contractorInfo.payerId')(newValue, newSelectedOption)
                        onPayerSelect(newValue, newSelectedOption)
                        setFieldTouched('contractorInfo.payerId')
                        setPayer(newSelectedOption)
                    }}
                    onContractorCreated={onContractorCreated}
                    role={"CLIENT"}
                    helperText={formatPayerLimitMessage()}
                />
            ]}
        {hasContractorReadPrivilege &&
            <>
                <AutosuggestionMailInput name="contractorInfo.emails"
                                         suggestions={clientMailSuggestions || []}
                                         placeholder={t('form.order.clientData.emails')}/>
                <MailField name="contractorInfo.bccEmails"
                           placeholder={t('form.order.clientData.bccEmails')}
                />
                <ReadOnlyCheckbox name="contractorInfo.requireCarrierVerifiedHV"
                                  checked={!!clientCopy?.requireCarrierVerifiedHV}
                                  label={t('form.order.clientData.requireCarrierVerifiedHV')}/>
            </>
        }
    </FormColumn>
}
